import { ExplorerTracking } from "@/lib/tracking/explorer";
import { useState } from "react";
import { Stack, Typography, Button } from "@altimateai/lego";
import { useAppState } from "@/modules/app/useAppContext";
import { useExplorerFiltersFromUrl } from "../../hooks";
import { useOpportunities } from "../../useOpportunity";
import type { SelectedRow, OwnerListItem } from "../../types";
import StatusAssignerDropdown from "../statusAssignerDropdown/StatusAssignerDropdown";
import OwnerAssignerDropdown from "../ownerAssignerDropdown/OwnerAssignerDropdown";
import styles from "../../styles.module.scss";

interface OpportunitiesBulkActionsProps {
  visible: boolean;
  selectedRows: SelectedRow[];
  ownerList: OwnerListItem[] | null;
  onApplySuccess: (newOwnerList: OwnerListItem[]) => void;
  onCancel: () => void;
}

export const OpportunitiesBulkActions = ({
  visible,
  selectedRows,
  ownerList,
  onApplySuccess,
  onCancel,
}: OpportunitiesBulkActionsProps) => {
  // Use null to represent "no selection", -1 for "Unassigned explicitly selected"
  const [assignedOwner, setAssignedOwner] = useState<number | null>(null);
  const [assignedStatus, setAssignedStatus] = useState<string | null>(null);

  const { startDate, endDate } = useExplorerFiltersFromUrl();
  const { mutation } = useOpportunities();
  const { mutate } = mutation;
  const { loggedinUser: user } = useAppState();

  const handleBulkApply = () => {
    const action = [assignedStatus && "status", assignedOwner !== null && "owner"]
      .filter(Boolean)
      .join("+");
    ExplorerTracking.bulkActionPerformed(action || "none", selectedRows.length);
    const changes = selectedRows.map(row => ({
      opportunity_id: row.opportunity_id,
      // Send "-1" for unassigned, owner ID if selected, null if not changed
      owner:
        assignedOwner === -1
          ? "-1"
          : assignedOwner !== null && assignedOwner > 0
            ? assignedOwner.toString()
            : null,
      status: assignedStatus,
    }));

    mutate(
      {
        changes,
        user_id: user?.id,
        start_date: startDate?.toISOString() || "",
        end_date: endDate?.toISOString() || "",
      },
      {
        onSuccess: () => {
          // Cache invalidation for /opportunities + /opportunities/implemented
          // is centralized in useOpportunities; we don't repeat it here.

          if (ownerList && assignedOwner !== null && assignedOwner > 0) {
            const updatedOwnerList = [...ownerList];
            selectedRows.forEach(row => {
              const existingIndex = updatedOwnerList.findIndex(
                item => item.opportunity_id === row.opportunity_id
              );

              if (existingIndex !== -1) {
                updatedOwnerList[existingIndex].owner = assignedOwner;
              } else {
                updatedOwnerList.push({
                  opportunity_id: row.opportunity_id,
                  owner: assignedOwner,
                });
              }
            });

            onApplySuccess(updatedOwnerList);
          } else {
            onCancel();
          }
        },
      }
    );
  };

  if (!visible) {
    return null;
  }

  return (
    <Stack
      role="region"
      aria-label="Bulk actions"
      align={"center"}
      justify={"between"}
      gap={"lg"}
      className={"al-px-4 al-py-1 al-rounded-md al-bg-brand al-w-full"}
    >
      <Typography className={"al-text-white"} variant={"body"} weight={"regular"} size={"sm"}>
        {`${selectedRows.length} item${selectedRows.length > 1 ? "s are" : " is"} selected`}
      </Typography>
      <Stack gap={"sm"} align={"center"}>
        <StatusAssignerDropdown
          setAssignedStatus={setAssignedStatus}
          assignedStatus={assignedStatus}
          disableUpdateOnChange={true}
          startDate={startDate?.toISOString()}
          endDate={endDate?.toISOString()}
        />
        <OwnerAssignerDropdown
          setAssignedOwner={(owner: number | string) =>
            setAssignedOwner(typeof owner === "number" ? owner : parseInt(owner, 10))
          }
          assignedOwner={
            assignedOwner !== null && assignedOwner > 0
              ? assignedOwner.toString()
              : assignedOwner === -1
                ? "-1"
                : undefined
          }
          disableUpdateOnChange={true}
          startDate={startDate?.toISOString()}
          endDate={endDate?.toISOString()}
        />
        <Button className={styles.apply} color="primary" onClick={handleBulkApply}>
          Apply
        </Button>
        <Button className={styles.cancel} color="primary" onClick={onCancel}>
          Cancel
        </Button>
      </Stack>
    </Stack>
  );
};
